

























































































































import { defineComponent, ref, reactive, useContext, computed, onMounted, onUnmounted } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import { useCheckoutStore } from '@/store/checkout'
import { formatPrice } from '@/common/utils/numbers'
import { Errors, mapExceptionToError, isInvalidTypePTMScan } from '~/composables/errors/useErrors'

import { useCountry } from '~/composables/country/useCountry'
import { usePriceBrick } from '~/composables/price-brick/usePriceBrick'
import { useI18n } from '~/composables/i18n'
import QuantityInput from '~/components/feature/quick-add/quantityInput.vue'
import PriceBrickFooter from '~/components/feature/price-brick/PriceBrickFooter.vue'
import { ProductAvailabilityAndPricing } from '~/plugins/pim.client'
import { PriceBrickSkuData } from '~/types/product/productTypes'
import ToolTip from '~/components/global/tooltip/ToolTip.vue'
import { useEbsStore } from '~/store/ebs'
import IconFaSolidBars from '~icons/fa-solid/info-circle'

export default defineComponent({
  components: {
    QuantityInput,
    PriceBrickFooter,
    ToolTip,
    IconFaSolidBars,
  },
  props: {
    skuData: {
      type: Array as () => PriceBrickSkuData[],
      required: true,
    },
    productId: {
      type: String,
      required: true,
    },
    productName: {
      type: String,
      required: true,
    },
    productType: {
      type: String,
      required: false,
      default: null,
    },
    includeInventory: {
      type: Boolean,
      default: false,
    },
    customFormulationLabel: {
      type: String,
      required: false,
      default: null,
    },
    templateTitle: {
      type: String,
      required: false,
      default: null,
    },
  },
  emits: ['addToBasket'],
  setup(props, { emit }) {
    const { $config, $pim } = useContext()
    const { t } = useI18n()
    const { addToBasketButtonText, showPrices, getCustomFormulationPath } = usePriceBrick()
    const {
      isUS,
      isJapan,
      isAT,
      isB2DCountry,
      isDE,
      isGB,
      isAU,
      isCA,
      isNZ,
      isFR,
      selectedCountry,
      isPunchoutCountries,
    } = useCountry()
    const { addToBasket } = useCheckoutStore()
    const ebsStore = useEbsStore()
    const { gopItems, showGOPAvailabilityItems, showGOPAvailability } = storeToRefs(ebsStore)
    const quantityChangesQueue = ref<Record<string, number>>({})
    let debounceTimeout: ReturnType<typeof setTimeout> | null = null

    const isPreviewServer = $config.cst.isPreview

    const skus = reactive(props.skuData)
    const errors = ref<Errors>([])

    const disabled = computed(() => isPreviewServer || !skus.some((q) => q.qty !== null && q.qty !== 0))

    const availabilities = ref<ProductAvailabilityAndPricing[]>([])
    onMounted(async () => {
      if (props.includeInventory) {
        availabilities.value = await $pim.getProductAvailabilitiesAndPricing(props.productId, selectedCountry.value)
      }

      if (showGOPAvailability.value) {
        ebsStore.fetchShipDates(
          selectedCountry.value,
          props.skuData.map((skuItem) => ({ sku: skuItem.sku, quantity: 1 }))
        )
      }
      hideTSizeSkuForJp()
    })

    onUnmounted(() => {
      ebsStore.$reset()
    })

    const resetInputs = () => {
      for (const sku of skus) {
        sku.qty = 0
      }

      if (showGOPAvailability.value) {
        ebsStore.fetchShipDates(
          selectedCountry.value,
          props.skuData.map((skuItem) => ({ sku: skuItem.sku, quantity: 1 }))
        )
      }
    }

    const onSkuQtyChange = (sku: string, quantity: number) => {
      quantity = quantity === 0 ? 1 : quantity

      if (gopItems.value[sku]?.quantity === quantity || !showGOPAvailability.value) {
        return
      }

      quantityChangesQueue.value[sku] = quantity
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }

      debounceTimeout = setTimeout(() => {
        ebsStore.fetchShipDates(
          selectedCountry.value,
          Object.keys(quantityChangesQueue.value).map((key) => ({
            sku: key,
            quantity: quantityChangesQueue.value[key],
          }))
        )
        quantityChangesQueue.value = {}
      }, 1000)
    }

    const onAddToBasket = async () => {
      const items = skus
        .filter((x) => x.qty !== null && x.qty !== 0)
        .map((x) => ({
          id: x.sku,
          quantity: x.qty,
        }))

      if (
        isPunchoutCountries.value ||
        isUS.value ||
        isDE.value ||
        isAT.value ||
        isGB.value ||
        isJapan.value ||
        isB2DCountry.value
      ) {
        errors.value = []
        try {
          await addToBasket(items)
          resetInputs()
        } catch (e: any) {
          errors.value = mapExceptionToError(
            e,
            'An error occurred while trying to add products to the cart. Please try again later or contact customer service.'
          )
        }
        // TODO: needs to be reworked once argocd env vars will be present
      } else if (isFR.value) {
        window.location.href = `${$config.vendor.ozymeApiHost}?product_search%5Bname%5D=${props.productId}&product_search%5Bproperties%5D%5B392%5D%5B%5D=3197`
      } else if (isCA.value) {
        window.location.href = `${$config.vendor.nebCanadaApiHost}?id=${props.productId}`
      } else if (isAU.value) {
        window.location.href = `${$config.vendor.nebAustraliaApiHost}?query=${props.productId}&country=en-au`
      } else if (isNZ.value) {
        window.location.href = `${$config.vendor.nebNewZealandApiHost}?query=${props.productId}&country=en-nz`
      } else {
        console.error('Error during add to basket call: Found no matching action for the current country')
        return
      }
      emit('addToBasket', { items, trackAsOldPriceBrick: false })
    }

    const customFormulationPath = computed(() => getCustomFormulationPath(props.productId, props.productName))

    // FIXME: revert once the JP wants to show those T sizes
    // this will hide unavailable T sizes with 0 getclp price from price brick
    const hideTSizeSkuForJp = () => {
      if (isJapan.value) {
        const skuToHide = availabilities.value.find(
          (a) => a.availability === '海外取寄せ' && a.pricing && a.pricing === '¥0.00' && a.sku.includes('T')
        )?.sku
        if (skuToHide) {
          const indexToRemove = skus.findIndex((sku) => sku.sku === skuToHide)
          if (indexToRemove >= 0) {
            skus.splice(indexToRemove, 1)
          }
        }
      }
    }

    return {
      t,
      errors,
      isUS,
      formatPrice,
      disabled,
      onAddToBasket,
      skus,
      customFormulationPath,
      isInvalidTypePTMScan,
      isB2DCountry,
      addToBasketButtonText,
      showPrices,
      availabilities,
      isPreviewServer,
      gopItems,
      showGOPAvailabilityItems,
      onSkuQtyChange,
    }
  },
})
